// import { message as $message, Modal } from 'ant-design-vue';
import type {AxiosRequestConfig, AxiosResponse} from 'axios'
import axios, {CanceledError} from 'axios'
import {isString} from 'lodash-es'
import {getShopId} from './shop'
import {getCurrentShopId, getShopifyAuthHeaders, validateShopifyAuthParams} from './shopifyAuth'
import {createApiSignHeaders} from './requestSigning'
import qs from 'qs'

const apiSignSecret = import.meta.env.VITE_API_SIGN_SECRET || ''

// 定义响应状态码枚举
export enum ResultEnum {
  SUCCESS = 200,
  ERROR = 500,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
}

// Create a single toast instance

export interface RequestOptions extends AxiosRequestConfig {
  /** 是否直接将数据从响应中提取出，例如直接返回 res.data，而忽略 res.code 等信息 */
  isReturnResult?: boolean
  /** 请求成功是提示信息 */
  successMsg?: string
  /** 请求失败是提示信息 */
  errorMsg?: string
  /** 成功时，是否显示后端返回的成功信息 */
  showSuccessMsg?: boolean
  /** 失败时，是否显示后端返回的失败信息 */
  showErrorMsg?: boolean
  requestType?: 'json' | 'form'
  url?: string
  /** 是否跳过Shopify认证（用于某些特殊接口） */
  skipShopifyAuth?: boolean,
  /** 是否使用baseApiUrl2 */
  useBaseApiUrl2?: boolean
}

const UNKNOWN_ERROR = '未知错误，请重试'

/** 真实请求的路径前缀 */
export const baseApiUrl1 = import.meta.env.VITE_API_BASE_URL || ''
export const baseApiUrl2 = import.meta.env.VITE_API_BASE_URL2 || ''

const controller = new AbortController()
const service = axios.create({
  baseURL: baseApiUrl1,
  timeout: 10000,
  signal: controller.signal,
  paramsSerializer(params) {
    return qs.stringify(params, { arrayFormat: 'brackets' })
  },
})

service.interceptors.request.use(
  config => {
    // 在这里可以添加token等认证信息
    // const token = localStorage.getItem('token')
    // if (token && config.headers) {
    //   config.headers['Authorization'] = `Bearer ${token}`
    // }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  (response: AxiosResponse<BaseResponse>) => {
    const res = response.data

    if (res.code !== ResultEnum.SUCCESS) {
      const error = new Error(res.message || UNKNOWN_ERROR) as Error & { code: any }
      error.code = res.code
      return Promise.reject(error)
    }
    // const sseStore = useSSEStore();
    // sseStore.setServerConnectStatus(true);
    return response
  },
  error => {
    if (!(error instanceof CanceledError)) {
      // 处理 422 或者 500 的错误异常提示
      // $message.error({ content: errMsg, key: errMsg });
      error.message = error?.response?.data?.message ?? UNKNOWN_ERROR
    }
    return Promise.reject(error)
  }
)

type BaseResponse<T = any> = {
  code: number
  message: string
  data: T
}

interface SuccessResponse<T> {
  success: boolean
  message: string
  data: T
}

/**
 * 构建请求头，包含Shopify认证信息
 */
function buildRequestHeaders(config: RequestOptions): Record<string, string> {
  const headers: Record<string, string> = {}

  // 添加用户自定义头部
  if (config.headers) {
    Object.entries(config.headers).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        headers[key] = String(value)
      }
    })
  }

  // 添加Shopify认证头（如果未跳过认证）
  if (!config.skipShopifyAuth) {
    const shopifyHeaders = getShopifyAuthHeaders()
    Object.assign(headers, shopifyHeaders)
  }

  // 添加商店ID（兼容旧版本）
  const shopId = getCurrentShopId() || getShopId()
  if (shopId) {
    headers['Shopify-Id'] = shopId
  }

  // 添加Content-Type（如果是表单请求）
  if (config.requestType === 'form') {
    headers['Content-Type'] = 'multipart/form-data'
  }

  return headers
}

export async function request<T = any>(
  _url: string | RequestOptions,
  _config: RequestOptions = {}
): Promise<BaseResponse<T>['data'] | BaseResponse<T> | SuccessResponse<T>> {
  const url = isString(_url) ? _url : _url.url
  const config = isString(_url) ? _config : _url
  // const { useBaseApiUrl2 = false } = config
  // 根据 useBaseApiUrl2 参数选择 baseURL
  const baseURL = baseApiUrl1
  
  try {
    const { requestType, isReturnResult = true, skipShopifyAuth = false, useBaseApiUrl2: _, ...rest } = config

    // 构建请求头
    const headers = buildRequestHeaders({
      ...config,
      skipShopifyAuth
    })

    const method = String(rest.method || 'GET').toUpperCase()
    if (apiSignSecret && url) {
      const signHeaders = await createApiSignHeaders(method, url, apiSignSecret)
      Object.assign(headers, signHeaders)
    }

    // 验证Shopify认证参数（如果未跳过认证）
    if (!skipShopifyAuth && !validateShopifyAuthParams()) {
      // 可以选择是否抛出错误，这里我们继续请求但记录警告
    }
    const response = (await service.request({
      url,
      ...rest,
      headers,
      baseURL,
    })) as AxiosResponse<BaseResponse<T>>
    const { data } = response
    const { code, message } = data || {}
    const hasSuccess = data && Reflect.has(data, 'code') && code === ResultEnum.SUCCESS

    if (hasSuccess) {
      const { successMsg, showSuccessMsg } = config
      if (successMsg) {
        return { success: true, message: successMsg, data: data.data } as SuccessResponse<T>
      } else if (showSuccessMsg && message) {
        return { success: true, message, data: data.data } as SuccessResponse<T>
      }
    }

    if (!isReturnResult) {
      return data
    } else {
      return data.data
    }
  } catch (error: any) {
    const { errorMsg, showErrorMsg } = config
    const errorMessage = errorMsg || (showErrorMsg ? error.message || '未知错误' : null)
    return Promise.reject({ error: true, message: errorMessage })
  }
}
